import { useCallback, useEffect, useState } from 'react'
import { adminApiService, type LogStorage, type PurgeLogsRequest } from '../../services/adminApiService'
import { ApiError } from '../../services/BaseApiService'
import { ConfirmModal } from '../../components/ui/ConfirmModal'
import { colors, fontSize, radius, spacing } from '../../styles/theme'

/** Level presets for "Delete older than N days". Values are the levels deleted; empty means every level. */
const LEVEL_FILTERS: { label: string; levels: string[] }[] = [
  { label: 'All levels', levels: [] },
  { label: 'Information and below (keep warnings and errors)', levels: ['Verbose', 'Debug', 'Information'] },
  { label: 'Debug and Verbose only', levels: ['Verbose', 'Debug'] },
  { label: 'Information only', levels: ['Information'] },
  { label: 'Warning only', levels: ['Warning'] },
]

/** Hard stop on the repeat loop, so a server that keeps answering "more" cannot spin the page forever. */
const MAX_PURGE_REQUESTS = 500

function formatKb(kb: number | null | undefined): string {
  if (kb == null) return '—'
  if (kb >= 1024 * 1024) return `${(kb / 1024 / 1024).toFixed(2)} GB`
  if (kb >= 1024) return `${(kb / 1024).toFixed(1)} MB`
  return `${kb} KB`
}

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().replace('T', ' ').slice(0, 16) + ' UTC'
}

const n = (v: number) => v.toLocaleString('en-US')

/**
 * Admin Logs page: size of doc.log_entry, the retention window RetentionCleanupJob applies, and manual
 * purges. Batched deletes are repeated until the server reports nothing left; "Delete all" truncates and
 * requires the confirmation phrase typed exactly. Every purge is recorded in the compliance log server-side.
 */
export default function LogCleanupPanel({ onPurged }: { onPurged?: () => void }) {
  const [storage, setStorage] = useState<LogStorage | null>(null)
  const [loadError, setLoadError] = useState('')
  const [open, setOpen] = useState(false)

  const [retentionInput, setRetentionInput] = useState('')
  const [savingRetention, setSavingRetention] = useState(false)
  const [retentionMessage, setRetentionMessage] = useState('')

  const [olderThanDays, setOlderThanDays] = useState('30')
  const [levelFilter, setLevelFilter] = useState(0)
  const [running, setRunning] = useState(false)
  const [progress, setProgress] = useState('')
  const [result, setResult] = useState('')
  const [actionError, setActionError] = useState('')

  const [confirmAll, setConfirmAll] = useState(false)
  const [typed, setTyped] = useState('')

  const load = useCallback(async () => {
    setLoadError('')
    try {
      const s = await adminApiService.getLogStorage()
      setStorage(s)
      setRetentionInput(String(s.retention.days))
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Failed to load log storage')
    }
  }, [])

  useEffect(() => { if (open && !storage) load() }, [open, storage, load])

  async function saveRetention(days: number | null) {
    setRetentionMessage('')
    if (days != null && storage && (!Number.isInteger(days) || days < storage.retention.minDays || days > storage.retention.maxDays)) {
      setRetentionMessage(`Enter a whole number of days from ${storage.retention.minDays} to ${storage.retention.maxDays}.`)
      return
    }
    setSavingRetention(true)
    try {
      const r = await adminApiService.setLogRetention(days)
      setStorage(s => (s ? { ...s, retention: r } : s))
      setRetentionInput(String(r.days))
      setRetentionMessage(days == null ? `Reset to the default (${r.days} days).` : `Saved. The next cleanup run keeps ${r.days} days.`)
    } catch (err) {
      setRetentionMessage(err instanceof ApiError ? err.message : 'Failed to save retention')
    } finally {
      setSavingRetention(false)
    }
  }

  /** Repeats one purge until the server says nothing is left, reporting the running total. */
  async function runPurge(first: PurgeLogsRequest, label: string) {
    setRunning(true)
    setActionError('')
    setResult('')
    let total = 0
    let req = first
    try {
      for (let i = 0; i < MAX_PURGE_REQUESTS; i++) {
        const r = await adminApiService.purgeLogs(req)
        total += r.deleted
        if (r.truncated) {
          setResult(`Deleted all logs (${n(r.deleted)} rows, table truncated).`)
          break
        }
        setProgress(`${label}: ${n(total)} rows deleted so far…`)
        if (!r.moreRemaining) {
          setResult(`${label}: ${n(total)} rows deleted.${r.fallback ? ' Truncate was not permitted for the database login, so rows were deleted in batches instead.' : ''}`)
          break
        }
        // Pin the cutoff the server chose on the first call, so later batches do not chase "now".
        req = r.cutoff ? { ...req, olderThanDays: undefined, before: r.cutoff } : req
      }
    } catch (err) {
      setActionError(`${err instanceof ApiError ? err.message : 'Purge failed'} (${n(total)} rows deleted before it stopped).`)
    } finally {
      setProgress('')
      setRunning(false)
      await load()
      onPurged?.()
    }
  }

  function deleteOlder() {
    const days = Number(olderThanDays)
    if (!Number.isInteger(days) || days < 0) { setActionError('Enter a whole number of days (0 or more).'); return }
    const levels = LEVEL_FILTERS[levelFilter].levels
    runPurge({ mode: 'olderThan', olderThanDays: days, levels: levels.length ? levels : undefined },
      `Older than ${days} day${days === 1 ? '' : 's'}`)
  }

  const phrase = storage?.confirmPhrase ?? 'DELETE ALL LOGS'

  function confirmDeleteAll() {
    setConfirmAll(false)
    setTyped('')
    runPurge({ mode: 'all', confirm: phrase }, 'Delete all logs')
  }

  const inputStyle = {
    padding: '5px 8px', border: `1px solid ${colors.border}`,
    borderRadius: radius.sm, fontSize: fontSize.sm, outline: 'none',
  } as const
  const buttonStyle = { ...inputStyle, cursor: 'pointer', background: colors.surface } as const
  const cell = { padding: spacing.xs, textAlign: 'right' as const }

  const s = storage?.stats
  const dbPct = s?.databaseMaxKb ? Math.round((s.databaseUsedKb / s.databaseMaxKb) * 100) : null

  return (
    <section style={{ border: `1px solid ${colors.border}`, borderRadius: radius.sm, marginBottom: spacing.md }}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        style={{ ...buttonStyle, border: 'none', width: '100%', textAlign: 'left', fontWeight: 600, padding: spacing.sm }}
      >
        {open ? '▾' : '▸'} Clean up logs
      </button>

      {open && (
        <div style={{ padding: spacing.md, borderTop: `1px solid ${colors.border}`, fontSize: fontSize.sm, color: colors.text }}>
          {loadError && <div role="alert" style={{ color: colors.error, marginBottom: spacing.sm }}>{loadError}</div>}
          {!storage && !loadError && <div style={{ color: colors.textSecondary }}>Loading…</div>}

          {storage && s && (
            <>
              <div style={{ display: 'flex', gap: spacing.lg, flexWrap: 'wrap', marginBottom: spacing.md }}>
                <div><div style={{ color: colors.textSecondary }}>Log rows (approx.)</div><strong>{n(s.approxRows)}</strong></div>
                <div><div style={{ color: colors.textSecondary }}>Log table size</div><strong>{formatKb(s.reservedKb)}</strong></div>
                <div>
                  <div style={{ color: colors.textSecondary }}>Database used</div>
                  <strong>{formatKb(s.databaseUsedKb)}{s.databaseMaxKb ? ` of ${formatKb(s.databaseMaxKb)} (${dbPct}%)` : ''}</strong>
                </div>
                <div><div style={{ color: colors.textSecondary }}>Oldest row</div><strong>{formatWhen(s.oldestAt)}</strong></div>
              </div>

              {storage.breakdownAvailable && storage.levels ? (
                <div style={{ overflowX: 'auto', marginBottom: spacing.md }}>
                  <table style={{ borderCollapse: 'collapse', fontSize: fontSize.xs, minWidth: 520 }}>
                    <thead>
                      <tr style={{ color: colors.textSecondary }}>
                        <th style={{ padding: spacing.xs, textAlign: 'left' }}>Level</th>
                        <th style={cell}>&lt; 1 day</th><th style={cell}>1–7 days</th><th style={cell}>7–30 days</th>
                        <th style={cell}>30–90 days</th><th style={cell}>&gt; 90 days</th><th style={cell}>Rows</th><th style={cell}>Size (approx.)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {storage.levels.map(l => (
                        <tr key={l.level} style={{ borderTop: `1px solid ${colors.border}` }}>
                          <td style={{ padding: spacing.xs }}>{l.level}</td>
                          <td style={cell}>{n(l.last24Hours)}</td><td style={cell}>{n(l.days1To7)}</td><td style={cell}>{n(l.days7To30)}</td>
                          <td style={cell}>{n(l.days30To90)}</td><td style={cell}>{n(l.over90Days)}</td>
                          <td style={cell}>{n(l.rows)}</td><td style={cell}>{formatKb(l.approxKb)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ color: colors.textSecondary, marginBottom: spacing.md }}>
                  Per-level counts are not available right now (the count timed out). Sizes above are exact.
                </div>
              )}

              <h3 style={{ fontSize: fontSize.sm, fontWeight: 600, margin: `${spacing.md} 0 ${spacing.xs}` }}>Retention</h3>
              <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', gap: spacing.xs, alignItems: 'center' }}>
                  Keep logs for
                  <input
                    type="number"
                    aria-label="Retention days"
                    min={storage.retention.minDays}
                    max={storage.retention.maxDays}
                    value={retentionInput}
                    onChange={e => setRetentionInput(e.target.value)}
                    style={{ ...inputStyle, width: 80 }}
                  />
                  days
                </label>
                <button type="button" disabled={savingRetention} onClick={() => saveRetention(Number(retentionInput))} style={buttonStyle}>
                  Save retention
                </button>
                {storage.retention.overrideDays != null && (
                  <button type="button" disabled={savingRetention} onClick={() => saveRetention(null)} style={buttonStyle}>
                    Reset to default ({storage.retention.defaultDays} days)
                  </button>
                )}
              </div>
              <div style={{ color: colors.textSecondary, marginTop: spacing.xs }}>
                {storage.retention.overrideDays != null
                  ? `Set from this page${storage.retention.updatedAt ? ` on ${formatWhen(storage.retention.updatedAt)}` : ''}; the configured default is ${storage.retention.defaultDays} days.`
                  : 'Using the configured default.'}
                {' '}Retention cleanup runs every {Math.round(storage.lastCleanup.intervalSeconds / 3600)} hours.
                {' '}Last run: {formatWhen(storage.lastCleanup.lastFinishedAt ?? storage.lastCleanup.lastStartedAt)}
                {storage.lastCleanup.lastOutcome ? ` (${storage.lastCleanup.lastOutcome})` : ''}
                {storage.lastCleanup.nextDueAt ? `; next due ${formatWhen(storage.lastCleanup.nextDueAt)}.` : '.'}
              </div>
              {retentionMessage && <div role="status" style={{ marginTop: spacing.xs }}>{retentionMessage}</div>}

              <h3 style={{ fontSize: fontSize.sm, fontWeight: 600, margin: `${spacing.md} 0 ${spacing.xs}` }}>Delete now</h3>
              <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', gap: spacing.xs, alignItems: 'center' }}>
                  Older than
                  <input
                    type="number"
                    aria-label="Older than days"
                    min={0}
                    value={olderThanDays}
                    onChange={e => setOlderThanDays(e.target.value)}
                    style={{ ...inputStyle, width: 80 }}
                  />
                  days
                </label>
                <select aria-label="Levels to delete" value={levelFilter} onChange={e => setLevelFilter(Number(e.target.value))} style={inputStyle}>
                  {LEVEL_FILTERS.map((f, i) => <option key={f.label} value={i}>{f.label}</option>)}
                </select>
                <button type="button" disabled={running} onClick={deleteOlder} style={buttonStyle}>Delete older logs</button>
                <button
                  type="button"
                  disabled={running}
                  onClick={() => { setTyped(''); setConfirmAll(true) }}
                  style={{ ...buttonStyle, color: colors.error, borderColor: colors.errorBorder }}
                >
                  Delete all logs…
                </button>
              </div>
            </>
          )}

          {progress && <div role="status" style={{ marginTop: spacing.sm, color: colors.textSecondary }}>{progress}</div>}
          {result && <div role="status" style={{ marginTop: spacing.sm, color: colors.success }}>{result}</div>}
          {actionError && <div role="alert" style={{ marginTop: spacing.sm, color: colors.error }}>{actionError}</div>}
        </div>
      )}

      <ConfirmModal
        open={confirmAll}
        title="Delete all logs"
        danger
        confirmLabel="Delete all logs"
        confirmDisabled={typed !== phrase}
        onCancel={() => { setConfirmAll(false); setTyped('') }}
        onConfirm={confirmDeleteAll}
        message={
          <>
            <p style={{ marginTop: 0 }}>
              This permanently deletes every application log row{s ? ` (about ${n(s.approxRows)})` : ''}. It cannot be undone.
            </p>
            <p>The compliance log keeps a record of who did this and when; it is not affected.</p>
            <label style={{ display: 'block' }}>
              Type <strong>{phrase}</strong> to confirm
              <input
                aria-label="Confirmation phrase"
                value={typed}
                onChange={e => setTyped(e.target.value)}
                autoComplete="off"
                style={{ ...inputStyle, display: 'block', width: '100%', marginTop: spacing.xs, boxSizing: 'border-box' }}
              />
            </label>
          </>
        }
      />
    </section>
  )
}
